/**
 * TranscriptionOutput Component
 * 
 * Displays transcribed text with optional editing capability.
 */

import React, { useState, useEffect, useRef } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SendIcon from '@mui/icons-material/Send';
import CompressIcon from '@mui/icons-material/Compress';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import { Lightbox } from './Lightbox';
import './TranscriptionOutput.css';

export interface TranscriptionOutputProps {
  value: string;
  onChange?: (text: string) => void;
  onSubmit?: () => void;
  onCondense?: () => void;
  placeholder?: string;
  editable?: boolean;
  disabled?: boolean;
  isCondensing?: boolean;
  variant?: 'positive' | 'negative' | 'neutral' | 'purple' | 'warning';
  showSubmitButton?: boolean;
}

export const TranscriptionOutput: React.FC<TranscriptionOutputProps> = ({
  value,
  onChange,
  onSubmit,
  onCondense,
  placeholder = 'Transcription will appear here...',
  editable = true,
  disabled = false,
  isCondensing = false,
  variant = 'neutral',
  showSubmitButton = true,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const [copied, setCopied] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    setEditValue(value);
  }, [value]);

  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.setSelectionRange(
        textareaRef.current.value.length,
        textareaRef.current.value.length
      );
    }
  }, [isEditing]);

  const handleEdit = () => {
    if (!editable || disabled) return;
    setIsEditing(true);
  };

  const handleSave = () => {
    setIsEditing(false);
    if (onChange && editValue !== value) {
      onChange(editValue);
    }
  };

  const handleCopy = async () => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleSubmit = () => {
    if (onSubmit && value.trim()) {
      onSubmit();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      e.preventDefault();
      handleSave();
      if (onSubmit) {
        onSubmit();
      }
    } else if (e.key === 'Escape') {
      setIsEditing(false);
      setEditValue(value);
    }
  };

  const handleFullscreen = () => {
    setIsFullscreen(true);
  };

  const handleFullscreenConfirm = (newValue: string) => {
    if (onChange && newValue !== value) {
      onChange(newValue);
    }
    setIsFullscreen(false);
  };

  const handleFullscreenClose = () => {
    setIsFullscreen(false);
  };

  const isEmpty = !value.trim();

  return (
    <div className={`transcription-output ${variant} ${disabled ? 'disabled' : ''}`}>
      <div className="transcription-header">
        <span className="transcription-label">Transcription</span>
        <div className="transcription-actions">
          {!isEmpty && (
            <>
              <button
                className="action-btn copy-btn"
                onClick={handleCopy}
                title="Copy to clipboard"
                disabled={disabled}
              >
                <ContentCopyIcon />
                {copied && <span className="copy-tooltip">Copied!</span>}
              </button>
              {editable && (
                <button
                  className={`action-btn edit-btn ${isEditing ? 'active' : ''}`}
                  onClick={isEditing ? handleSave : handleEdit}
                  title={isEditing ? 'Save' : 'Edit'}
                  disabled={disabled}
                >
                  {isEditing ? <CheckIcon /> : <EditIcon />}
                </button>
              )}
              <button
                className="action-btn fullscreen-btn"
                onClick={handleFullscreen}
                title="Fullscreen"
                disabled={disabled}
              >
                <FullscreenIcon />
              </button>
            </>
          )}
        </div>
      </div>
      
      <div className="transcription-content">
        {isEditing ? (
          <textarea
            ref={textareaRef}
            className="transcription-textarea"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleSave}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            disabled={disabled}
          />
        ) : (
          <div className={`transcription-text nodrag ${isEmpty ? 'empty' : ''}`}>
            {isEmpty ? placeholder : value}
          </div>
        )}
      </div>

      {showSubmitButton && !isEmpty && (
        <div className="transcription-footer">
          {onCondense && (
            <button
              className="condense-btn"
              onClick={onCondense}
              disabled={disabled || isEmpty || isCondensing}
              title="Condense transcription using AI"
            >
              <CompressIcon />
              <span>{isCondensing ? 'Condensing...' : 'Condense'}</span>
            </button>
          )}
          <button
            className="submit-btn"
            onClick={handleSubmit}
            disabled={disabled || isEmpty}
            title="Send transcription (Ctrl+Enter)"
          >
            <SendIcon />
            <span>Send</span>
          </button>
        </div>
      )}

      {/* Fullscreen Lightbox */}
      {isFullscreen && (
        <Lightbox
          imageUrl=""
          content={value}
          contentType="text"
          onClose={handleFullscreenClose}
          onConfirm={handleFullscreenConfirm}
          showConfirmButtons={editable}
        />
      )}
    </div>
  );
};

export default TranscriptionOutput;
